import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AssessmentsList from "@/components/account/AssessmentsList";
import DangerZone from "@/components/account/DangerZone";

const ensureUids = (rows) =>
  (Array.isArray(rows) ? rows : []).map((r) =>
    r && r._uid ? r : { ...r, _uid: `av_${Math.random().toString(36).slice(2, 10)}` }
  );

// Kept exported so AdminConsole can reuse the same shape helpers — they live
// in Account.jsx historically and removing them would break the AdminConsole's
// local copy. Keep until we extract a shared `lib/admin.js`.
export { ensureUids };

const settingsFromApi = (data) => ({  // eslint-disable-line no-unused-vars
  consultant_email: data?.consultant_email || "",
  booking_email: data?.booking_email || "",
  booking_timezone: data?.booking_timezone || "Europe/London",
  booking_lead_hours: data?.booking_lead_hours ?? 24,
  booking_horizon_days: data?.booking_horizon_days ?? 21,
  booking_availability: ensureUids(data?.booking_availability),
  paywall_effective_from: data?.paywall_effective_from || "",
});

export default function Account() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/"); return; }
    (async () => {
      try {
        const { data } = await api.get("/account/assessments");
        setRows(data.assessments || []);
      } catch (e) {
        console.error(e);
        toast.error("Could not load your assessments.");
      } finally {
        setLoading(false);
      }
    })();
  }, [user, authLoading, navigate]);

  const deleteAll = async (confirmText) => {
    if (confirmText !== "DELETE") {
      toast.error("Type DELETE to confirm.");
      return;
    }
    try {
      setDeleting(true);
      await api.delete("/account/me");
      await logout();
      toast.success("Your account and data have been deleted.");
      navigate("/");
    } catch (e) {
      console.error(e);
      toast.error("Deletion failed. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 flex flex-col">
      <Header />
      <main className="flex-1 max-w-5xl mx-auto w-full px-6 lg:px-10 py-14">
        <p className="text-[11px] uppercase tracking-[0.18em] text-teal-700 dark:text-teal-400">Account</p>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl font-semibold text-slate-900 dark:text-slate-50 tracking-tight">
          Your assessments
        </h1>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
          Signed in as <span className="text-slate-900 dark:text-slate-50 font-medium">{user?.email}</span>
        </p>

        <AssessmentsList rows={rows} loading={loading} />

        <DangerZone onDelete={deleteAll} deleting={deleting} />
      </main>
      <Footer />
    </div>
  );
}
